/**
 * usePermissionState Hook
 * Manages permission state and provides handlers for permission requests
 */

import { useState, useEffect, useCallback } from 'react'
import { PermissionState, checkPermissionState, getPermissionErrorMessage } from '../helpers/permissionHelpers'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'

interface UsePermissionStateProps {
  onPermissionsGranted: () => void
}

interface UsePermissionStateReturn {
  permissionState: PermissionState
  errorMessage: string
  isRequestingPermissions: boolean
  hasCheckedPermissions: boolean
  handleRequestPermissions: () => Promise<void>
  handleCheckAgain: () => Promise<void>
}

export const usePermissionState = ({ onPermissionsGranted }: UsePermissionStateProps): UsePermissionStateReturn => {
  const { t } = useTranslation()
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false)
  const [hasCheckedPermissions, setHasCheckedPermissions] = useState(false)
  const [permissionState, setPermissionState] = useState<PermissionState>('checking')
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Check if permissions are already granted on mount
  useEffect(() => {
    const checkExistingPermissions = async () => {
      const state = await checkPermissionState()
      setPermissionState(state)
      setHasCheckedPermissions(true)

      if (state === 'granted') {
        onPermissionsGranted()
      }
    }

    checkExistingPermissions()
  }, [onPermissionsGranted])

  const handleRequestPermissions = useCallback(async () => {
    setIsRequestingPermissions(true)
    setErrorMessage('')

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })

      // Stop the stream immediately - we just needed permissions
      stream.getTracks().forEach((track) => track.stop())

      setPermissionState('granted')
      onPermissionsGranted()
    } catch (error) {
      const mediaError = error as DOMException

      const errorMsg = getPermissionErrorMessage(mediaError, t)

      if (mediaError.name === 'NotAllowedError' || mediaError.name === 'PermissionDeniedError') {
        setPermissionState('denied')
      } else {
        setPermissionState('error')
      }

      setErrorMessage(errorMsg)
    } finally {
      setIsRequestingPermissions(false)
    }
  }, [onPermissionsGranted, t])

  const handleCheckAgain = useCallback(async () => {
    setIsRequestingPermissions(true)
    setErrorMessage('')

    try {
      const state = await checkPermissionState()
      setPermissionState(state)

      if (state === 'granted') {
        onPermissionsGranted()
      } else if (state === 'denied') {
        setErrorMessage(t('gamer.permissionScreen.errors.stillDenied'))
      } else if (state === 'error') {
        setErrorMessage(t('gamer.permissionScreen.errors.generic'))
      }
    } catch (error) {
      console.error('[usePermissionState] Error in handleCheckAgain:', error)
      setErrorMessage(t('gamer.permissionScreen.errors.generic'))
    } finally {
      setIsRequestingPermissions(false)
    }
  }, [onPermissionsGranted, t])

  return {
    permissionState,
    errorMessage,
    isRequestingPermissions,
    hasCheckedPermissions,
    handleRequestPermissions,
    handleCheckAgain
  }
}
